import React from 'react';

import {RenderPaginationProps} from './types';
import {useCombinedRefs} from './hooks';

export type TabPaginationConsumerProps<T extends HTMLElement = HTMLElement> = {
    onKeyDown(e: React.KeyboardEvent): void;
    onBulletClick(index: number): (e: React.MouseEvent) => void;
    ref: React.RefObject<T>;
};

type TabPaginationProps<T extends HTMLElement = HTMLElement> = Omit<
    RenderPaginationProps,
    'paginationLabel'
> & {
    children(props: TabPaginationConsumerProps<T>): React.ReactNode;
};

export const focusTab = <T extends HTMLElement>(
    index: number,
    ref: React.RefObject<T>
) => {
    const list = ref.current;
    if (!list) return;

    const listItem = list.children;
    if (!listItem[index]) return;

    (listItem[index] as HTMLElement).focus({
        preventScroll: true
    });
};

export const TabPagination = React.forwardRef(
    <T extends HTMLElement = HTMLElement>(
        {
            children,
            activeIndex,
            paginationData,
            onSlideSelect
        }: TabPaginationProps<T>,
        ref: React.ForwardedRef<T>
    ) => {
        const tabListRef = useCombinedRefs(ref);

        const handleBulletClick = React.useCallback(
            (index: number) => (e: React.MouseEvent) => {
                onSlideSelect(index);
                focusTab(index, tabListRef);
                e.preventDefault();
            },
            [onSlideSelect, tabListRef]
        );

        const handleKeyDown = React.useCallback(
            (e: React.KeyboardEvent): void => {
                let newIndex = activeIndex;

                switch (e.key) {
                    case 'ArrowLeft': {
                        newIndex =
                            (paginationData.length + activeIndex - 1) %
                            paginationData.length;
                        break;
                    }
                    case 'ArrowRight': {
                        newIndex = (activeIndex + 1) % paginationData.length;
                        break;
                    }
                    case 'Home': {
                        newIndex = 0;
                        break;
                    }
                    case 'End': {
                        newIndex = paginationData.length - 1;
                        break;
                    }
                    default:
                        break;
                }
                if (newIndex === activeIndex) return;

                onSlideSelect(newIndex);
                focusTab(newIndex, tabListRef);
                e.preventDefault();
            },
            [activeIndex, paginationData.length, onSlideSelect, tabListRef]
        );

        return (
            <>
                {children({
                    onKeyDown: handleKeyDown,
                    onBulletClick: handleBulletClick,
                    ref: tabListRef as React.RefObject<T>
                })}
            </>
        );
    }
) as <T extends HTMLElement = HTMLElement>(
    props: TabPaginationProps<T> & {ref?: React.ForwardedRef<T>}
) => React.ReactElement;
